// KPI Builder — the request the live preview sends, and what comes back, turned into what ChartRenderer draws.
import { ChartSeries } from 'helpers/chartDataProcessors';

export interface PreviewRange {
  /** yyyy-mm-dd */
  startDate: string;
  endDate: string;
}

export interface PreviewRequest {
  visualizationCode: string;
  /** Query array as the backend stores it: requestQueryMap and aggrQuery stay JSON strings. */
  queries: unknown[];
  config: Record<string, unknown>;
  handlerKey?: string;
  filters: PreviewRange;
}

/** responseData of the preview call: the chart payload the dashboard would get, plus any per-query failures. */
export interface PreviewResponse {
  data?: unknown;
  customData?: { previewErrors?: unknown[]; previewWarnings?: unknown[] };
}

/** The editor's fields, as text, that make up a visualization. The dashboard filter list (kpiFilters) is not part of what runs. */
export interface PreviewSource {
  code: string;
  queries: string;
  config: string;
  handlerKey?: string;
}

/** A number tile's value as the backend reports it. */
export interface PreviewMetric {
  value?: number;
  /** 'number' or 'percentage'. */
  symbol: string;
  isDecimal: boolean;
  insight?: string;
}

export interface PreviewChart {
  /** Series exactly as the dashboard draws them; undefined for a number tile. */
  data: ChartSeries[] | undefined;
  metric?: PreviewMetric;
  errors: string[];
  warnings: string[];
}

/** The endpoint wants a date range, so "all the data" is everything from before the platform existed up to today. */
export const allTimeRange = (now: Date = new Date()): PreviewRange => ({ startDate: '2000-01-01', endDate: now.toISOString().slice(0, 10) });

const parse = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const isRecord = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);

/** The preview request for what is typed in the editor, or null while the query or config is not usable JSON yet. */
export function buildPreviewRequest(src: PreviewSource, range: PreviewRange): PreviewRequest | null {
  const queries = parse(src.queries);
  const config = parse(src.config);
  if (!Array.isArray(queries) || queries.length === 0 || !isRecord(config)) return null;
  // The builder's own answers are not part of what the backend runs.
  const { builder: _builder, ...rest } = config;
  return {
    visualizationCode: src.code || 'previewDraft',
    queries,
    config: rest,
    ...(src.handlerKey ? { handlerKey: src.handlerKey } : {}),
    filters: range,
  };
}

const firstNumber = (...candidates: unknown[]): number | undefined =>
  candidates.map((c) => (c === '' || c === null || c === undefined ? NaN : Number(c))).find((n) => Number.isFinite(n));

/** A number tile's value, wherever the backend put it in the first row. */
function metricOf(data: unknown): PreviewMetric {
  const row = ((Array.isArray(data) ? data[0] : data) ?? {}) as Record<string, unknown>;
  const plots = Array.isArray(row.plots) ? (row.plots as Record<string, unknown>[]) : [];
  return {
    value: firstNumber(row.headerValue, row.count, row.value, plots[0]?.value),
    symbol: String(row.headerSymbol ?? 'number'),
    isDecimal: row.isDecimal === true,
    insight: typeof row.insight === 'string' && row.insight ? row.insight : undefined,
  };
}

/** The number as the dashboard shows it: Indian digit grouping, decimals only when the KPI says so, % for percentages. */
export function formatMetric(m: PreviewMetric): string {
  if (m.value === undefined) return '—';
  const text = m.value.toLocaleString('en-IN', { maximumFractionDigits: m.isDecimal ? 2 : 0 });
  return /^(percent|%)/i.test(m.symbol) ? `${text}%` : text;
}

/** How many rows/bars/slices the preview shows before "Show all". */
export const PREVIEW_ROW_LIMIT = 10;

/** Charts that are a list or a ranking, where showing the first rows is a fair preview. A pie or a line cut short would misrepresent the data, so they are drawn whole. */
export const CLIPPED_CHART_TYPES: string[] = ['table', 'tableBar', 'bar', 'horizontalBar', 'funnel', 'conversionFunnel', 'stageFunnel'];

/** A table has one series per row, so its length is the number of series; a table preview is cut to this few rows. */
export const TABLE_PREVIEW_ROWS = 3;

const TABLE_TYPES: string[] = ['table', 'tableBar'];

/** The number of rows/bars/slices the chart would draw, which is what the preview clips. */
export const chartLength = (chartType: string, data: ChartSeries[]): number => (TABLE_TYPES.includes(chartType) ? data.length : largestSeries(data));

/** How many rows/bars/slices the preview shows before "View all". A table is cut shorter than the rest, since each row is wide. */
export const previewLimit = (chartType: string): number => (TABLE_TYPES.includes(chartType) ? TABLE_PREVIEW_ROWS : PREVIEW_ROW_LIMIT);

/** The data with only the first rows/bars/slices the preview shows. */
export const clipChart = (chartType: string, data: ChartSeries[]): ChartSeries[] =>
  TABLE_TYPES.includes(chartType) ? data.slice(0, previewLimit(chartType)) : limitPlots(data, previewLimit(chartType));

/** The most plots any one series has: the length of the longest list the chart would draw. */
export const largestSeries = (data: ChartSeries[]): number => data.reduce((max, s) => Math.max(max, s.plots?.length ?? 0), 0);

/** The same series with at most `limit` plots each, so a long table or ranking does not fill the screen. */
export const limitPlots = (data: ChartSeries[], limit: number = PREVIEW_ROW_LIMIT): ChartSeries[] =>
  data.map((s) => (s.plots && s.plots.length > limit ? { ...s, plots: s.plots.slice(0, limit) } : s));

const describeError =(e: unknown): string => (typeof e === 'string' ? e : JSON.stringify(e));
const messages = (list?: unknown[]): string[] => (list ?? []).map(describeError);

/** What to hand to ChartRenderer (or the number tile) for this chart type, and what the backend reported about the queries. */
export function previewToChart(chartType: string, response: PreviewResponse): PreviewChart {
  const { previewErrors, previewWarnings } = response.customData ?? {};
  const base = { errors: messages(previewErrors), warnings: messages(previewWarnings) };
  if (chartType === 'metric') return { ...base, data: undefined, metric: metricOf(response.data) };
  return { ...base, data: Array.isArray(response.data) ? (response.data as ChartSeries[]) : undefined };
}
